// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/
;globalThis["_sentryRouteManifest"] = "{\"isrRoutes\":[],\"dynamicRoutes\":[],\"staticRoutes\":[]}";globalThis["_sentryNextJsVersion"] = "16.3.6";globalThis["_sentryRewritesTunnelPath"] = "/monitoring";import * as Sentry from '@sentry/nextjs';

import { getAmplitudeErrorContext } from '@/utils/amplitude';

import baseConfig from '../sentry.base.config';

Sentry.init({
  ...baseConfig,
  integrations: [Sentry.browserTracingIntegration()],
  ignoreErrors: [
    // Random plugins/extensions
    'top.GLOBALS',
    // produced by rabby wallet on windows https://0x-f3.sentry.io/issues/4591794883
    "'defineProperty' on proxy: trap returned falsish for property 'request'",
    // See: http://blog.errorception.com/2012/03/tale-of-unfindable-js-error.html
    'originalCreateNotification',
    'canvas.contentDocument',
    'MyApp_RemoveAllHighlights',
    'http://tt.epicplay.com',
    "Can't find variable: ZiteReader",
    'jigsaw is not defined',
    'ComboSearch is not defined',
    'http://loading.retry.widdit.com/',
    'atomicFindClose',
    // Facebook borked
    'fb_xd_fragment',
    // ISP "optimizing" proxy - `Cache-Control: no-transform` seems to reduce this. (thanks @acdha)
    // See http://stackoverflow.com/questions/4113268/how-to-stop-javascript-injection-from-vodafone-proxy
    'bmi_SafeAddOnload',
    'EBCallBackMessageReceived',
    // See http://toolbar.conduit.com/Developer/HtmlAndGadget/Methods/JSInjection.aspx
    'conduitPage',
    // Generic error code from errors outside the security sandbox
    // You can delete this if using raven.js > 1.0, which ignores these automatically.
    'Script error.',
    // Avast extension error
    '_avast_submit',
    // contract.symbol() fails on bytes32
    'method="symbol()"',
    // WalletConnect relay receives session events for topics deleted server-side
    // or stale in localStorage; internal to @walletconnect/sign-client, not actionable.
    // Sentry issue MATCHA-V3-Q4E
    'No matching key. session',
    // Privy rejects when an injected wallet provider stops answering RPC. The
    // provider owns the timeout, and a single stuck session can emit it repeatedly.
    // Sentry issue MATCHA-V3-THY
    'Wallet timeout',
  ],
  beforeSend(event) {
    // Filter events when the user cancels the wallet connection
    if (event.extra && JSON.stringify(event.extra).includes('User rejected the request')) {
      return null;
    }

    // Injected wallet providers reject fire-and-forget requests with a plain
    // EIP-1193 object (code 4900/4901: provider disconnected) when the wallet is
    // locked or offline. These surface as unhandled rejections with no stacktrace
    // and originate in connector/extension code, not ours; caught paths already
    // classify these codes as RPC_DISCONNECTED. Sentry issue MATCHA-V3-5XQ.
    const serialized = event.extra?.__serialized__;
    if (typeof serialized === 'object' && serialized !== null) {
      const code = (serialized as { code?: unknown }).code;
      if (code === 4900 || code === 4901) {
        return null;
      }
    }

    // Stamp every event with the Amplitude replay link + device/session ids so any
    // error (including unhandled ones that bypass reportError) traces back to the
    // user's Amplitude activity. Read per-event rather than via a global scope tag
    // because the replay id only becomes available partway through a recorded
    // session. Guarded: this runs on every event and must never throw, or it would
    // drop the error report itself.
    try {
      const { deviceId, sessionId, sessionReplayId, sessionReplayUrl } = getAmplitudeErrorContext();
      // Sentry tags are string-valued facets: skip absent ids instead of writing
      // undefined keys, and stringify the numeric session id.
      const tags: Record<string, string> = {};
      if (deviceId) tags['amplitude.device_id'] = deviceId;
      if (sessionId !== undefined) tags['amplitude.session_id'] = String(sessionId);
      if (sessionReplayId) tags['amplitude.session_replay_id'] = sessionReplayId;
      event.tags = { ...event.tags, ...tags };
      event.contexts = {
        ...event.contexts,
        amplitude: { deviceId, sessionId, sessionReplayId, sessionReplayUrl },
      };
    } catch {
      // Amplitude unavailable — send the event without correlation.
    }

    return event;
  },
  allowUrls: [
    /https?:\/\/([a-z0-9]+[.])*matcha\.xyz/,
    /https:\/\/[a-zA-Z0-9-]+\.ingest\.sentry\.io/,
  ],
  denyUrls: [
    // Google Adsense
    /pagead\/js/i,
    // Facebook flakiness
    /graph\.facebook\.com/i,
    // Facebook blocked
    /connect\.facebook\.net\/en_US\/all\.js/i,
    // Woopra flakiness
    /eatdifferent\.com\.woopra-ns\.com/i,
    /static\.woopra\.com\/js\/woopra\.js/i,
    // Chrome extensions
    /extensions\//i,
    /^chrome:\/\//i,
    // Other plugins
    /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
    /webappstoolbarba\.texthelp\.com\//i,
    /metrics\.itunes\.apple\.com\.edgesuite\.net\//i,
  ],
});

export const onRouterTransitionStart = Sentry.captureRouterTransitionStart;
